import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import IncomingRenewalTable from '@components/charts/membership/analytics-overview/IncomingRenewalTable'
import { formatInteger } from '@components/charts/membership/revenue/figures'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import TableSection from '@components/charts/membership/revenue/TableSection'
import useRevenueLinks from '@components/charts/membership/revenue/useRevenueLinks'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphUpcomingRenewalsBucket,
} from '@models/membership/MembershipAnalyticsGraph'
import { MembershipAnalyticsCurrency } from '@models/membership/MembershipAnalyticsStatistics'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Col, Flex, Row, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

const { Text } = Typography

// Figma 1024:2620: one tile per bucket, 3px coloured left border. Buckets are
// matched by the API's `key`, not by position.
const BUCKETS = [
  {
    key: '0-30d',
    title: 'Next 30 days',
    color: '#1677ff',
    tooltip: 'Renewals due between today and 30 days from now.',
  },
  {
    key: '31-60d',
    title: '31–60 days',
    color: '#13c2c2',
    tooltip: 'Renewals due between 31 and 60 days from now.',
  },
  {
    key: '61-90d',
    title: '61–90 days',
    color: '#8c8c8c',
    tooltip: 'Renewals due between 61 and 90 days from now.',
  },
]

/**
 * "Upcoming renewals" (Figma 1024:2613 / 1024:2620 / 1024:2660): header with a
 * link, three bucket tiles from today, then the next-5-renewals preview.
 */
interface Props {
  /** Program currency (the graph carries none), from the page's attention stats. */
  currency?: MembershipAnalyticsCurrency
}

const UpcomingRenewals = ({ currency }: Props) => {
  const link = useRevenueLinks()
  const { selectedMembershipPrograms, isMembershipProgramReady } =
    useAnalytics()

  const [buckets, setBuckets] = useState<
    MembershipAnalyticsGraphUpcomingRenewalsBucket[]
  >([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>()
  const latest = useRef(0)

  useEffect(() => {
    if (!isMembershipProgramReady) return

    // Relative to today and independent of the period: the endpoint still
    // wants a window, so it gets today.
    const today = dayjs().format('YYYY-MM-DD')
    const params: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'upcoming-renewals',
      startDate: today,
      endDate: today,
      programIds: selectedMembershipPrograms,
    }

    const requestId = ++latest.current
    setIsLoading(true)
    setError(undefined)

    fetchAnalyticsGraph<MembershipAnalyticsGraphUpcomingRenewalsBucket[]>(
      params
    )
      .then(({ data }) => {
        if (requestId !== latest.current) return
        // A bare array: no currency in the graph, it comes from the attention
        // statistics below.
        setBuckets(data ?? [])
        if (!data) setError('No data available.')
      })
      .catch((e) => {
        if (requestId !== latest.current) return
        console.error('Error fetching data:', e)
        // Cleared: an error next to the previous program's counts would read
        // as if they were current.
        setBuckets([])
        setError('An error occurred while fetching data.')
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [selectedMembershipPrograms, isMembershipProgramReady])

  return (
    <Flex vertical gap={24}>
      <SectionHead
        title="Upcoming renewals"
        caption="From today · not affected by the selected period"
        extra={
          <Link
            to={link('/membership/revenue/renewals')}
            style={{ whiteSpace: 'nowrap' }}
          >
            View all renewals →
          </Link>
        }
      />
      <Row gutter={[24, 24]} align="stretch">
        {BUCKETS.map((bucket) => {
          const item = buckets.find((candidate) => candidate.key === bucket.key)
          return (
            <Col xs={24} md={8} key={bucket.title}>
              <AnalyticsStatistic
                variant="overview"
                title={bucket.title}
                tooltip={bucket.tooltip}
                accent={bucket.color}
                value={formatInteger(item?.count)}
                secondary={
                  item && typeof item.amount === 'number'
                    ? `${analyticsCurrency(item.amount, false, currency)} to collect`
                    : undefined
                }
                isLoading={isLoading}
              />
            </Col>
          )
        })}
      </Row>
      {error && <Text type="danger">{error}</Text>}
      <TableSection
        title="Renewals in the next 7 days"
        caption="Preview · next 5 renewals"
      >
        <IncomingRenewalTable />
      </TableSection>
    </Flex>
  )
}

export default UpcomingRenewals
